// M12.5 — server-safe inline-SVG time-series builder.
//
// Consumed by both the dashboard (TimeSeriesChart mirrors this shape
// in React for interactivity) and the PDF template (`print-html.ts`
// concatenates the returned string into DocRaptor-friendly HTML).
//
// Uses pure math/d3 modules only (scale + shape + array + time). No
// DOM, no React, no `document`. Safe inside Cloudflare Workers where
// `buildPrintHtml` runs. Returns a fully-formed `<svg>…</svg>` string
// that Prince 10.1 and every mainstream browser can render.

import { extent, max } from "d3-array";
import { scaleLinear, scaleTime } from "d3-scale";
import { line as d3Line } from "d3-shape";

export interface TimeSeriesPoint {
  /** ISO YYYY-MM-DD. */
  date: string;
  value: number;
}

export interface BuildTimeSeriesSvgOpts {
  width?: number;
  height?: number;
  /** Per-series labels. When length > 1 a legend row renders above the plot. */
  labels?: string[];
  /** Per-series stroke colours (hex or CSS colour name). */
  colors?: string[];
  /** Axis label for the y-axis (rendered small, left of the plot). */
  yLabel?: string;
  /** Informs axis tick density. Charts themselves don't resample —
   *  callers pre-aggregate their points at the chosen granularity. */
  granularity: "day" | "week" | "month";
}

const DEFAULT_WIDTH = 720;
const DEFAULT_HEIGHT = 220;
const DEFAULT_COLOR = "#9583FF"; // purple-500 — matches design-system tokens
const AXIS_COLOR = "#6B7280"; // neutral-500
const GRID_COLOR = "#E5E7EB"; // neutral-200
const LEGEND_TEXT = "#374151"; // neutral-700

const MARGIN = { top: 24, right: 16, bottom: 28, left: 40 };

/**
 * Build a static SVG line chart from one or more pre-aggregated series.
 * Each series is a list of `{ date, value }` points sorted ascending by
 * date. Empty series are tolerated (the chart renders with axes only).
 */
export function buildTimeSeriesSvg(
  series: TimeSeriesPoint[][],
  opts: BuildTimeSeriesSvgOpts,
): string {
  const width = opts.width ?? DEFAULT_WIDTH;
  const height = opts.height ?? DEFAULT_HEIGHT;
  const innerW = width - MARGIN.left - MARGIN.right;
  const innerH = height - MARGIN.top - MARGIN.bottom;

  // Flatten once for shared x/y extents. Using spreads keeps the arg
  // count manageable (typed arrays would fight us in Workers).
  const allPoints = series.flat();
  if (allPoints.length === 0) {
    return emptyChart(width, height, opts.yLabel);
  }

  const dates = allPoints.map((p) => new Date(`${p.date}T00:00:00Z`));
  const [xMin, xMax] = extent(dates) as [Date, Date];
  const yMaxRaw = max(allPoints, (p) => p.value) ?? 0;
  // Nudge yMax so the top line doesn't flush against the top edge.
  const yMax = yMaxRaw === 0 ? 1 : yMaxRaw * 1.08;

  const xScale = scaleTime().domain([xMin, xMax]).range([0, innerW]);
  const yScale = scaleLinear().domain([0, yMax]).range([innerH, 0]);

  const lineGen = d3Line<TimeSeriesPoint>()
    .x((p) => xScale(new Date(`${p.date}T00:00:00Z`)))
    .y((p) => yScale(p.value));

  // ---- axis ticks -------------------------------------------------------
  const xTickCount = opts.granularity === "day" ? 6 : opts.granularity === "week" ? 5 : 4;
  const yTickCount = 4;

  const xTicks = xScale.ticks(xTickCount);
  const yTicks = yScale.ticks(yTickCount);

  const xTickLabels = xTicks.map((d) => formatXTick(d, opts.granularity));
  const yTickLabels = yTicks.map((v) => formatYTick(v));

  // ---- legend row -------------------------------------------------------
  const labels = opts.labels ?? [];
  const colors = opts.colors ?? [];
  const showLegend = labels.length > 1;

  let legend = "";
  if (showLegend) {
    const swatchSize = 10;
    const rowY = 10;
    const chipGap = 16;
    let cursorX = MARGIN.left;
    const chips: string[] = [];
    for (let i = 0; i < labels.length; i += 1) {
      const color = colors[i] ?? DEFAULT_COLOR;
      const label = labels[i];
      // Approximate text width by char count (Gotham-ish) so we can
      // place the next chip. Overflow is clipped by the outer svg
      // viewBox — legends with > ~6 labels should wrap via caller.
      const textWidth = label.length * 6 + 4;
      const chipWidth = swatchSize + 4 + textWidth + chipGap;
      chips.push(
        `<rect x="${cursorX}" y="${rowY - swatchSize + 2}" width="${swatchSize}" height="${swatchSize}" rx="2" fill="${escapeAttr(color)}"></rect>` +
          `<text x="${cursorX + swatchSize + 4}" y="${rowY + 1}" font-size="10" fill="${LEGEND_TEXT}" font-family="inherit">${escapeText(label)}</text>`,
      );
      cursorX += chipWidth;
    }
    legend = chips.join("");
  }

  // ---- gridlines + axes -------------------------------------------------
  const gridlines = yTicks
    .map((v) => {
      const y = yScale(v);
      return `<line x1="0" y1="${y}" x2="${innerW}" y2="${y}" stroke="${GRID_COLOR}" stroke-width="1" />`;
    })
    .join("");

  const xAxisTicks = xTicks
    .map((d, i) => {
      const x = xScale(d);
      return (
        `<line x1="${x}" y1="${innerH}" x2="${x}" y2="${innerH + 4}" stroke="${AXIS_COLOR}" stroke-width="1" />` +
        `<text x="${x}" y="${innerH + 16}" font-size="10" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">${escapeText(xTickLabels[i])}</text>`
      );
    })
    .join("");

  const yAxisTicks = yTicks
    .map((v, i) => {
      const y = yScale(v);
      return `<text x="-8" y="${y + 3}" font-size="10" fill="${AXIS_COLOR}" text-anchor="end" font-family="inherit">${escapeText(yTickLabels[i])}</text>`;
    })
    .join("");

  // ---- paths ------------------------------------------------------------
  const paths = series
    .map((points, i) => {
      if (points.length === 0) return "";
      const color = colors[i] ?? DEFAULT_COLOR;
      const d = lineGen(points);
      if (!d) return "";
      return `<path d="${d}" fill="none" stroke="${escapeAttr(color)}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />`;
    })
    .join("");

  const yLabelSvg = opts.yLabel
    ? `<text x="${-MARGIN.left + 4}" y="${-10}" font-size="9" font-weight="700" letter-spacing="1" fill="${AXIS_COLOR}" font-family="inherit">${escapeText(opts.yLabel.toUpperCase())}</text>`
    : "";

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Time series chart">` +
    legend +
    `<g transform="translate(${MARGIN.left}, ${MARGIN.top})">` +
    yLabelSvg +
    gridlines +
    xAxisTicks +
    yAxisTicks +
    paths +
    `</g>` +
    `</svg>`
  );
}

function emptyChart(width: number, height: number, yLabel?: string): string {
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" role="img" aria-label="Time series chart (empty)">` +
    `<rect x="0" y="0" width="${width}" height="${height}" fill="#FAFAFA"></rect>` +
    `<text x="${width / 2}" y="${height / 2}" font-size="12" fill="${AXIS_COLOR}" text-anchor="middle" font-family="inherit">${escapeText(yLabel ? `No ${yLabel.toLowerCase()} data` : "No data")}</text>` +
    `</svg>`
  );
}

function formatXTick(d: Date, granularity: "day" | "week" | "month"): string {
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const day = d.getUTCDate();
  if (granularity === "month") {
    return `${month[d.getUTCMonth()]} ${String(d.getUTCFullYear()).slice(2)}`;
  }
  return `${month[d.getUTCMonth()]} ${day}`;
}

function formatYTick(v: number): string {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 10_000) return `${(v / 1_000).toFixed(0)}K`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  // Integer-ish thresholds — drop trailing .0 so ticks read "400" not "400.0"
  if (Math.abs(v - Math.round(v)) < 0.05) return String(Math.round(v));
  return v.toFixed(1);
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;");
}

function escapeText(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
